import React, { useCallback, useMemo, useState } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, ScrollView } from 'react-native';
import { useNavigation } from '@react-navigation/native';

import { DesktopAdminLayout } from '@/components/common/admin/desktop/DesktopAdminLayout';
import { DesktopPageHeader } from '@/components/common/admin/desktop/DesktopPageHeader';
import { FormSection, FormInput } from '@/components';
import { RecipientSelector } from '@/components/admin/messages/CreateMessage/components/RecipientSelector';
import { MessageTypeSelector } from '@/components/admin/messages/CreateMessage/components/MessageTypeSelector';
import { FileUploadSection } from '@/components/common/admin/mobile/FileUploadSection';
import { useCreateConversationMutation } from '@/store/api/admin/messagesApi';
import { useAdminUser } from '@/hooks/admin/useAdminUser';
import { useToast, useErrorToast } from '@/contexts/admin/ToastContext';
import { SVG_COMPONENTS } from '@/assets/admin';
import { useCreateMessageForm } from '@/components/admin/messages/CreateMessage/hooks/useCreateMessageForm';
import { GroupChatConflictNotice } from '@/components/admin/messages/CreateMessage/components/GroupChatConflictNotice';
import { UnassignedRecipientsNotice } from '@/components/admin/messages/CreateMessage/components/UnassignedRecipientsNotice';
import { buildCreateConversationFormData } from '@/components/admin/messages/CreateMessage/utils/formData';
import { WebConfirmModal } from '@/components/common/modals/desktop/DesktopConfirmModal';
import { useAdminColors } from '@/hooks/admin/useAdminColors';
import { GlassCard } from '@/components/shared/redesign';
import { GlassFormInput } from '@/components/admin/redesign';

export const DesktopCreateMessageView: React.FC = () => {
  const navigation = useNavigation<any>();
  const { redesign, admin } = useAdminColors();
  const { showSuccess } = useToast();
  const { showApiError } = useErrorToast();
  const adminUser = useAdminUser();
  const selectedPropertyId = adminUser.selectedPropertyId;

  const [createConversation, { isLoading }] = useCreateConversationMutation();
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);

  const {
    formData,
    errors,
    validateForm,
    handleFieldChange,
    showDiscardAlert,
    hasUnsavedChanges,
    isFormValid,
    getRecipientStats,
  } = useCreateMessageForm();

  const disableSend = useMemo(() => {
    if (isLoading) return true;
    if (!formData.subject || !formData.messageContent) return true;
    if (formData.recipients.length === 0) return true;
    return false;
  }, [isLoading, formData.subject, formData.messageContent, formData.recipients.length]);

  const handleBack = useCallback(() => {
    // Always confirm on desktop to match requested behavior
    setShowCancelConfirm(true);
  }, []);

  const handleSubmit = useCallback(async () => {
    if (!selectedPropertyId) {
      showApiError('No property selected');
      return;
    }

    if (isLoading || disableSend) {
      return;
    }

    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      showApiError('Please fix the form errors before submitting');
      return;
    }

    try {
      const formDataToSend = buildCreateConversationFormData(formData as any, selectedPropertyId);
      const result = await createConversation(formDataToSend).unwrap();
      if (result?.success) {
        showSuccess('Message created successfully');
        navigation.goBack();
      } else {
        showApiError('Failed to create message');
      }
    } catch (error: any) {
      const errorMessage = error?.data?.message || 'Failed to create message';
      showApiError(errorMessage);
    }
  }, [selectedPropertyId, isLoading, disableSend, validateForm, formData, createConversation, showSuccess, showApiError, navigation]);

  const recipientStats = getRecipientStats();

  return (
    <DesktopAdminLayout>
      <DesktopPageHeader title="New Message">
        <View style={styles.headerActions}>
          <TouchableOpacity
            style={[styles.cancelButton, { backgroundColor: redesign.card.bg }]}
            onPress={handleBack}
          >
            <Text style={[styles.cancelText, { color: redesign.text.primary }]}>Cancel</Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[
              styles.sendButton,
              { backgroundColor: admin.formInput.borderFocused },
              disableSend && {
                backgroundColor: redesign.card.bg,
                opacity: 0.6,
              },
            ]}
            onPress={handleSubmit}
            disabled={disableSend}
          >
            <Text style={styles.sendText}>Send</Text>
          </TouchableOpacity>
        </View>
      </DesktopPageHeader>

      <View style={styles.row}>
        {/* Left pane: form */}
        <GlassCard padding={0} style={styles.leftPane}>
          <ScrollView style={styles.leftScroll} contentContainerStyle={styles.leftContent}>
            <FormSection title="Message Details">
              <FormInput
                label="Subject"
                value={formData.subject}
                onChangeText={(value) => handleFieldChange('subject', value)}
                placeholder="Enter message subject"
                error={errors.subject}
                required
                maxLength={100}
              />
            </FormSection>

            <RecipientSelector
              recipients={formData.recipients}
              onRecipientsChange={(recipients) => handleFieldChange('recipients', recipients)}
              error={errors.recipients}
              messageType={formData.messageType}
            />

            <MessageTypeSelector
              messageType={formData.messageType}
              onMessageTypeChange={(type) => handleFieldChange('messageType', type)}
              recipientCount={formData.recipients.length}
            />

            {/* Group chat conflict (guarantors with mixed leases) */}
            <GroupChatConflictNotice mismatchedGuarantors={formData.mismatchedGuarantors as any} />

            {/* Unassigned recipients (invitation flow) */}
            <UnassignedRecipientsNotice recipients={formData.recipients as any} />

            <FileUploadSection
              title="Attachments"
              subtitle="Add files to your message (optional)"
              files={formData.files}
              onFilesChange={(files) => handleFieldChange('files', files)}
              maxFiles={5}
            />

            <FormSection title="Notification Settings">
              <TouchableOpacity
                onPress={() => handleFieldChange('sendEmail', !formData.sendEmail)}
                activeOpacity={0.8}
              >
                <GlassCard padding={16} style={styles.toggleCard}>
                  <View style={styles.toggleRow}>
                    <View style={styles.toggleContent}>
                      <Text style={[styles.toggleLabel, { color: redesign.text.primary }]}>
                        Send email notification
                      </Text>
                      <Text style={[styles.toggleSubtext, { color: redesign.text.muted }]}>
                        Recipients will receive an email notification about this message
                      </Text>
                    </View>
                    <View
                      style={[
                        styles.toggle,
                        { borderColor: redesign.card.border },
                        formData.sendEmail && {
                          backgroundColor: admin.formInput.borderFocused,
                          borderColor: admin.formInput.borderFocused,
                        },
                      ]}
                    >
                      {formData.sendEmail && (
                        <SVG_COMPONENTS.CHECK_CIRCLE width={16} height={16} color="#FFFFFF" />
                      )}
                    </View>
                  </View>
                </GlassCard>
              </TouchableOpacity>
            </FormSection>
          </ScrollView>
        </GlassCard>

        {/* Right pane: compose */}
        <GlassCard padding={0} style={styles.rightPane}>
          <View
            style={[
              styles.composeHeader,
              { borderBottomColor: redesign.card.border },
            ]}
          >
            <View style={styles.composeHeaderText}>
              <Text style={[styles.composeTitle, { color: redesign.text.primary }]} numberOfLines={1}>
                {formData.subject || 'Untitled message'}
              </Text>
              <Text style={[styles.composeSubtitle, { color: redesign.text.muted }]} numberOfLines={1}>
                {recipientStats.total > 0
                  ? `${recipientStats.total} Recipient${recipientStats.total === 1 ? '' : 's'}`
                  : 'No recipients selected'}
              </Text>
            </View>
          </View>

          <View style={styles.composeBody}>
            <GlassFormInput
              value={formData.messageContent}
              onChangeText={(v) => handleFieldChange('messageContent', v)}
              placeholder="Your message..."
              multiline
              maxLength={1000}
              textAlignVertical="top"
              inputStyle={styles.textInput}
              containerStyle={styles.textInputContainer}
            />

            {formData.files?.length > 0 && (
              <View style={styles.attachSummary}>
                <SVG_COMPONENTS.PAPERCLIP width={16} height={16} color={redesign.text.muted} />
                <Text style={[styles.attachSummaryText, { color: redesign.text.muted }]}>
                  {formData.files.length} attachment{formData.files.length === 1 ? '' : 's'}
                </Text>
              </View>
            )}
          </View>

          <View
            style={[
              styles.footerBar,
              { borderTopColor: redesign.card.border },
            ]}
          >
            <TouchableOpacity
              style={[
                styles.footerSendButton,
                { backgroundColor: admin.formInput.borderFocused },
                disableSend && {
                  backgroundColor: redesign.card.bg,
                  opacity: 0.6,
                },
              ]}
              onPress={handleSubmit}
              disabled={disableSend}
            >
              <Text style={styles.footerSendText}>Send Message</Text>
            </TouchableOpacity>
          </View>
        </GlassCard>
      </View>
      <WebConfirmModal
        visible={showCancelConfirm}
        title={'Discard Changes'}
        message={'You have unsaved changes. Are you sure you want to discard them?'}
        confirmText={'Discard'}
        cancelText={'Keep Editing'}
        onConfirm={() => {
          setShowCancelConfirm(false);
          navigation.goBack();
        }}
        onCancel={() => setShowCancelConfirm(false)}
      />
    </DesktopAdminLayout>
  );
};

const styles = StyleSheet.create({
  headerActions: {
    flexDirection: 'row',
    gap: 8,
  },
  cancelButton: {
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderRadius: 8,
  },
  cancelText: {
    fontWeight: '600',
  },
  sendButton: {
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 8,
  },
  sendText: {
    color: '#FFFFFF',
    fontWeight: '700',
  },
  row: {
    flexDirection: 'row',
    gap: 12,
    flex: 1,
    minHeight: 360,
  },
  leftPane: {
    width: 420,
    maxWidth: 480,
    alignSelf: 'stretch',
  },
  leftScroll: {
    flex: 1,
  },
  leftContent: {
    padding: 12,
    paddingBottom: 24,
  },
  rightPane: {
    flex: 1,
    alignSelf: 'stretch',
  },
  composeHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
  },
  composeHeaderText: {
    flex: 1,
    marginRight: 12,
  },
  composeTitle: {
    fontSize: 18,
    fontWeight: '600',
    marginBottom: 2,
  },
  composeSubtitle: {
    fontSize: 14,
  },
  composeBody: {
    flex: 1,
    padding: 16,
    gap: 12,
  },
  textInputContainer: {
    flex: 1,
    marginBottom: 0,
  },
  textInput: {
    minHeight: 160,
    fontSize: 16,
  },
  attachSummary: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  attachSummaryText: {
    fontSize: 14,
  },
  // Toggle styles (desktop)
  toggleCard: {
    marginTop: 8,
  },
  toggleRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  toggleContent: {
    flex: 1,
    marginRight: 16,
  },
  toggleLabel: {
    fontSize: 16,
    fontWeight: '500',
    marginBottom: 4,
  },
  toggleSubtext: {
    fontSize: 14,
    lineHeight: 18,
  },
  toggle: {
    width: 24,
    height: 24,
    borderRadius: 4,
    borderWidth: 2,
    backgroundColor: 'transparent',
    alignItems: 'center',
    justifyContent: 'center',
  },
  footerBar: {
    padding: 12,
    borderTopWidth: 1,
  },
  footerSendButton: {
    paddingVertical: 12,
    borderRadius: 10,
    alignItems: 'center',
  },
  footerSendText: {
    color: '#FFFFFF',
    fontWeight: '700',
  },
});

export default DesktopCreateMessageView;
